import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { getUatTermsStatus, acknowledgeUatTerms } from '../services/uatTermsService';
import { logout } from '../services/authService';
import { serverMessage } from '../services/errors';
import './UatTermsGate.css';

/**
 * Blocks the authenticated application until the tester has e-signed the UAT
 * test-environment notice. **UAT-only** — this component exists on the
 * `public-testing` branch and nowhere else.
 *
 * Rendered from `ProtectedRoute`, which is the single component both layout tiers
 * wrap, so this covers the whole authenticated surface including `/select-agency`
 * (where a master-claim INITIAL_ADMIN lands). Placing it there also means no route
 * JSX changes, which matters because `appRouting.test.js` parses `App.js` source and
 * is byte-sensitive about the guard-route lines.
 *
 * It renders the notice **instead of** `children` rather than over the top of them.
 * A scrim would leave the app mounted underneath, fetching data and painting screens
 * behind a wall the tester has not agreed to yet.
 *
 * Dialog mechanics follow `DeleteAgencyDialog` — focusable set recomputed on every
 * Tab so a disabled button is never the wrap-around target, focus restored on unmount.
 * Escape and overlay-click are deliberately **absent**: there is no legitimate way to
 * dismiss this, and offering one that silently does nothing is worse than offering none.
 */

/**
 * Set once the tester accepts, so the one remount that happens in practice — moving
 * between the `BareLayout` and `Layout` tiers, e.g. `/select-agency` → `/` — does not
 * re-ask the server. Deliberately module state and not a third `sessionStorage` key:
 * `logout()` and `api.js`'s `clearSession()` both remove only `token` and `user`, so a
 * third key would survive a sign-out and leak into the next tester's session in the
 * same tab. Every sign-in is a full page load (`hardRedirect`), so this resets itself.
 */
let satisfiedThisLoad = false;

/** Test seam: page-load state has to be resettable between test cases. */
export const __resetUatTermsGateForTests = () => {
  satisfiedThisLoad = false;
};

function UatTermsGate({ children }) {
  const [status, setStatus] = useState(satisfiedThisLoad ? 'ok' : 'loading');
  const [notice, setNotice] = useState(null);
  const [version, setVersion] = useState(null);
  const [signedName, setSignedName] = useState('');
  const [readToEnd, setReadToEnd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const dialogRef = useRef(null);
  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setError('');
    try {
      const data = await getUatTermsStatus();
      if (data.accepted) {
        satisfiedThisLoad = true;
        setStatus('ok');
        return;
      }
      setNotice(data.document);
      setVersion(data.version);
      setStatus('required');
    } catch (err) {
      // Fail CLOSED. A consent gate that opens itself when the server is unreachable
      // is not a consent gate — the tester would be inside the application having
      // agreed to nothing, and the audit table would show no reason why.
      setError(serverMessage(err, 'Could not load the testing notice.'));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!satisfiedThisLoad) {
      load();
    }
  }, [load]);

  /**
   * The signature is enabled only once the notice has actually been scrolled through.
   * A document shorter than its container never scrolls, so that case counts as read
   * rather than locking the tester out of a notice they can already see in full.
   */
  const checkScrolledToEnd = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    // 2px of slack: fractional scroll positions under browser zoom otherwise leave
    // scrollTop a hair short of the bottom and the button never enables.
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 2) {
      setReadToEnd(true);
    }
  }, []);

  useEffect(() => {
    if (status === 'required') {
      checkScrolledToEnd();
    }
  }, [status, notice, checkScrolledToEnd]);

  // Focus trap. Runs when the dialog appears, not on mount, because the dialog is
  // absent during 'loading'.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || status !== 'required') return undefined;

    const enabledFocusable = () => Array.from(dialog.querySelectorAll(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled),'
      + ' textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'
    ));

    const previouslyFocused = document.activeElement;
    enabledFocusable()[0]?.focus();

    const handleKeyDown = (e) => {
      // No Escape branch on purpose — see the component docblock.
      if (e.key !== 'Tab') return;
      const focusable = enabledFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    dialog.addEventListener('keydown', handleKeyDown);
    return () => {
      dialog.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [status]);

  const canSign = readToEnd && signedName.trim().length >= 2;

  const submit = async (decision) => {
    if (submitting) return;
    if (decision === 'ACCEPTED' && !canSign) return;
    setSubmitting(true);
    setError('');
    try {
      await acknowledgeUatTerms({
        version,
        signedName: signedName.trim(),
        decision,
      });
      if (decision === 'ACCEPTED') {
        satisfiedThisLoad = true;
        setStatus('ok');
      } else {
        // Clears the session and hard-navigates to /login, so this component goes away.
        logout();
      }
    } catch (err) {
      setError(serverMessage(err, 'Could not record your response. Please try again.'));
      setSubmitting(false);
    }
  };

  if (status === 'ok') {
    return children;
  }

  if (status === 'loading') {
    // Deliberately blank rather than a spinner: the request is a single indexed
    // existence check and a flash of "Loading…" reads worse than a beat of nothing.
    return <div className="uat-terms-gate-blank" />;
  }

  if (status === 'error') {
    return (
      <div className="uat-terms-gate-overlay">
        <div className="uat-terms-gate-panel uat-terms-gate-panel--narrow" role="alert">
          <h1 className="uat-terms-gate-title">
            <AlertTriangle size={20} aria-hidden="true" />
            Testing notice unavailable
          </h1>
          <p className="uat-terms-gate-lede">{error}</p>
          <p className="uat-terms-gate-lede">
            You need to review and agree to the testing notice before using this
            environment, so the application stays locked until it loads.
          </p>
          <div className="uat-terms-gate-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => logout()}
            >
              Sign out
            </button>
            <button type="button" className="btn btn-primary" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="uat-terms-gate-overlay">
      <div
        className="uat-terms-gate-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="uat-terms-gate-title"
        ref={dialogRef}
      >
        <div className="uat-terms-gate-header">
          <h1 id="uat-terms-gate-title" className="uat-terms-gate-title">
            <AlertTriangle size={20} aria-hidden="true" />
            {notice?.title}
          </h1>
          <p className="uat-terms-gate-meta">
            Version {notice?.version}
            {notice?.effectiveDate ? ` · Effective ${notice.effectiveDate}` : ''}
          </p>
        </div>

        <div
          className="uat-terms-gate-body"
          ref={bodyRef}
          onScroll={checkScrolledToEnd}
          tabIndex={0}
          role="region"
          aria-label="Testing notice"
        >
          {notice?.intro && <p className="uat-terms-gate-lede">{notice.intro}</p>}

          {(notice?.sections ?? []).map((section) => (
            <section className="uat-terms-gate-section" key={section.heading}>
              <h2 className="uat-terms-gate-heading">{section.heading}</h2>
              <ul className="uat-terms-gate-clauses">
                {(section.clauses ?? []).map((clause) => (
                  <li key={clause}>{clause}</li>
                ))}
              </ul>
            </section>
          ))}

          {notice?.closing && <p className="uat-terms-gate-closing">{notice.closing}</p>}
        </div>

        <div className="uat-terms-gate-footer">
          {!readToEnd && (
            <p className="uat-terms-gate-hint" role="status">
              Scroll to the end of the notice to sign.
            </p>
          )}

          <label className="uat-terms-gate-label" htmlFor="uat-terms-signature">
            Type your full legal name to sign
          </label>
          <input
            id="uat-terms-signature"
            className="uat-terms-gate-input"
            type="text"
            value={signedName}
            autoComplete="off"
            disabled={!readToEnd || submitting}
            onChange={(e) => setSignedName(e.target.value)}
          />

          {error && (
            <p className="uat-terms-gate-error" role="alert">{error}</p>
          )}

          <div className="uat-terms-gate-actions">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={submitting}
              onClick={() => submit('DECLINED')}
            >
              Decline and sign out
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canSign || submitting}
              onClick={() => submit('ACCEPTED')}
            >
              I agree
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UatTermsGate;
